import React, { useState, useEffect, useContext } from 'react';
import { Block } from '../../../../../../../types/shared/blocksV3';
import FeetInchesInput from '../../FeetInchesInputV2';
import EdgeLabel from '../../../../../../../enums/edgeLabel';
import {
	Box,
	FormControlLabel,
	Radio,
	RadioGroup,
	Stack,
	styled,
	Typography,
} from '@mui/material';
import { toastError } from 'src/Toaster/Toast';
import {
	MIN_STAIR_WIDTH,
	MIN_LANDING_DEPTH,
	MAX_DECK_DEPTH,
	MAX_DECK_WIDTH,
	MAX_DECK_HEIGHT,
} from 'src/constants/limits';
import getParentBlock from 'src/utils/getParentBlockV3';
import { DeckContext } from '../../../context/DeckContextV3';
import { getVacantSegmentWithoutValidation } from '../../../utils/getVacantSegment';
import { useQuoteState } from 'src/stateProviders/QuoteStateProvider';

const StyledLabel = styled(Typography)({
	fontWeight: 500,
	fontSize: 14,
});

const LandingPopup: React.FC<{
	block: Block;
	blockData?: Block;
	setBlockData?: (block: Block) => void;
}> = ({
	block,
	blockData: externalBlockData,
	setBlockData: externalSetBlockData,
}) => {
	const { state } = useContext(DeckContext);
	const quoteState = useQuoteState();

	const [internalBlockData, setInternalBlockData] = useState(block);
	const blockData = externalBlockData || internalBlockData;
	const setBlockData = externalSetBlockData || setInternalBlockData;
	const [reinitialize, setReinitialize] = useState(0);

	const { parentBlock } = getParentBlock(state.blocks, block.parentBlock);

	// Reset internal state when block changes
	useEffect(() => {
		setInternalBlockData(block);
	}, [block.id]);

	const blockAttachedEdge = block.edges.find((ed) => ed.attachedToParent);

	const displayWidthDimension =
		blockAttachedEdge?.label == 'Top' || blockAttachedEdge?.label == 'Bottom'
			? 'width'
			: 'depth';

	const parentEdge = parentBlock.edges.find(
		(edge) => edge.id == block.parentBlock.edgeId
	);

	let segmentAttachedTo;
	let segmentIndex;

	if (parentEdge) {
		segmentIndex = parentEdge.segments.findIndex(
			(segment) => segment.id == block.parentBlock.segmentId
		);

		segmentAttachedTo = parentEdge.segments[segmentIndex];
	}

	const handleWidthChange = (inches) => {
		if (inches === null || inches === undefined) return;

		if (inches < MIN_STAIR_WIDTH) {
			toastError({
				title: 'Width',
				description: `${block.label} width cannot be less than ${MIN_STAIR_WIDTH / 12} ft`,
			});

			inches = MIN_STAIR_WIDTH;
			setReinitialize(Date.now());
		}

		//    Identify parent as Stair via parentBlock.type
		const isAttachedToStair = parentBlock?.type === 'Stair';
		if (isAttachedToStair) {
			// The landing is attached to the END of the stair. The stair's width is along the same axis as landing width.
			const stairWidth = parentBlock?.dimensions?.width || 0;
			if (inches < stairWidth) {
				toastError({
					title: 'Landing Width',
					description:
						'Landing width must be greater than or equal to stair width',
				});
				inches = Math.max(inches, stairWidth);
				setReinitialize(Date.now());
			}
		}

		const dimensionAlongEdge =
			displayWidthDimension == 'width' ? inches : blockData.dimensions.depth;

		if (
			!isAttachedToStair &&
			segmentAttachedTo &&
			dimensionAlongEdge > segmentAttachedTo.length &&
			parentEdge
		) {
			const vacantSegment = getVacantSegmentWithoutValidation({
				blocks: state.blocks,
				selectedBlockEdge: parentEdge,
			});

			const segmentsLength = parentEdge.segments.reduce(
				(acc, segment) => acc + segment.length,
				0
			);

			const availableLength =
				(vacantSegment?.length || 0) + (parentEdge.length - segmentsLength);

			if (availableLength < dimensionAlongEdge - segmentAttachedTo.length) {
				toastError({
					title: 'Width',
					description: `Edge ${parentEdge?.label} has no space to expand  ${block.label} to ${dimensionAlongEdge / 12}ft `,
				});

				inches = blockData.dimensions.width;
				setReinitialize(Date.now());
			}
		}

		setBlockData({
			...blockData,
			dimensions: {
				width: inches,
				depth: blockData.dimensions.depth,
				height: blockData.dimensions.height,
			},
			displayDimensions: {
				width: inches,
				height: blockData.dimensions.depth,
			},
			edges: blockData.edges.map((edge) => {
				if (edge.label == EdgeLabel.TOP || edge.label == EdgeLabel.BOTTOM) {
					return {
						...edge,
						length: inches,
						segments: edge.segments.map((seg) => ({
							...seg,
							length: inches,
						})),
					};
				}
				return edge;
			}),
		});
	};

	const handleDepthChange = (inches) => {
		if (inches < MIN_LANDING_DEPTH) {
			toastError({
				title: 'Depth',
				description: `${block.label} depth cannot be less than ${MIN_LANDING_DEPTH / 12} ft`,
			});
			inches = MIN_LANDING_DEPTH;
			setReinitialize(Date.now());
		}

		const dimensionAlongEdge =
			displayWidthDimension == 'width' ? blockData.dimensions.width : inches;

		if (
			segmentAttachedTo &&
			dimensionAlongEdge > segmentAttachedTo.length &&
			parentEdge
		) {
			const vacantSegment = getVacantSegmentWithoutValidation({
				blocks: state.blocks,
				selectedBlockEdge: parentEdge,
			});

			const segmentsLength = parentEdge.segments.reduce(
				(acc, segment) => acc + segment.length,
				0
			);

			const availableLength =
				(vacantSegment?.length || 0) + (parentEdge.length - segmentsLength);

			if (availableLength < dimensionAlongEdge - segmentAttachedTo.length) {
				toastError({
					title: 'Depth',
					description: `Edge ${parentEdge?.label} has no space to expand  ${block.label} to ${dimensionAlongEdge / 12}ft `,
				});

				inches = blockData.dimensions.depth;
				setReinitialize(Date.now());
			}
		}

		setBlockData({
			...blockData,
			dimensions: {
				width: blockData.dimensions.width,
				depth: inches,
				height: blockData.dimensions.height,
			},
			displayDimensions: {
				width: blockData.dimensions.width,
				height: inches,
			},
			edges: blockData.edges.map((edge) => {
				if (edge.label == EdgeLabel.LEFT || edge.label == EdgeLabel.RIGHT) {
					return {
						...edge,
						length: inches,
						segments: edge.segments.map((seg) => ({
							...seg,
							length: inches,
						})),
					};
				}
				return edge;
			}),
		});
	};

	return (
		<Stack spacing={1.25}>
			{quoteState.reface && (
				<RadioGroup
					value={blockData.reface ? 'Reface' : 'Rebuild'}
					onChange={(e) => {
						setBlockData({
							...blockData,
							reface: e.target.value == 'Reface',
						});
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'start',
						}}
					>
						<FormControlLabel
							value={'Reface'}
							control={<Radio />}
							label={<StyledLabel>Reface</StyledLabel>}
						/>
						<FormControlLabel
							value={'Rebuild'}
							control={<Radio />}
							label={<StyledLabel>Rebuild</StyledLabel>}
						/>
					</Box>
				</RadioGroup>
			)}

			<FeetInchesInput
				valueInches={blockData.dimensions.depth}
				labelInches="Depth"
				onChange={handleDepthChange}
				reinitialize={reinitialize}
				toastLabel="Landing Depth"
				maxFeet={MAX_DECK_DEPTH / 12}
			/>

			<FeetInchesInput
				valueInches={blockData.dimensions.width}
				labelInches="Width"
				onChange={handleWidthChange}
				reinitialize={reinitialize}
				toastLabel="Landing Width"
				maxFeet={MAX_DECK_WIDTH / 12}
			/>

			<FeetInchesInput
				valueInches={blockData.dimensions.height}
				labelInches="Height"
				onChange={() => {}}
				locked={true}
				toastLabel="Landing Height"
				maxFeet={MAX_DECK_HEIGHT / 12}
			/>
		</Stack>
	);
};

export default LandingPopup;
